import { Controller, useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { CollapsibleSection } from '../primitives/CollapsibleSection';
import { CoverImageTooltip } from '../primitives/CoverImageTooltip';
import { FieldLabel } from '../primitives/FieldLabel';
import { MediaDropField } from '../primitives/MediaDropField';
import { ProgramColorField } from '../primitives/ProgramColorField';
import { useSectionOpenState } from '../../lib/useSectionOpenState';
import * as styles from './PresentationMediaSection.css';
import { ProgramFormValues } from '../../model/programForm.types';

type PresentationMediaSectionProps = {
  isSubmitting?: boolean;
};

const CoverImageInfoIcon = () => (
  <CoverImageTooltip>
    <FontAwesomeIcon
      icon={faInfoCircle}
      className={styles.infoIcon}
      tabIndex={0}
      role="button"
      aria-label="Cover image information"
    />
  </CoverImageTooltip>
);

export const PresentationMediaSection = ({
  isSubmitting,
}: PresentationMediaSectionProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<ProgramFormValues>();
  const { isOpen, setIsOpen } = useSectionOpenState();

  return (
    <CollapsibleSection
      title="Presentation & media"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <div className={styles.row}>
        <Controller
          control={control}
          name="mediaCover"
          render={({ field }) => (
            <div className={styles.mediaColumn}>
              <FieldLabel text="Cover image" hint={<CoverImageInfoIcon />} />
              <div className={styles.mediaBox}>
                <MediaDropField
                  value={field.value}
                  onChange={field.onChange}
                  format="image"
                  accept={{ 'image/*': [] }}
                  disabled={isSubmitting}
                  testId="program-form-cover"
                />
              </div>
              {errors.mediaCover?.message ? (
                <div className={styles.error}>{errors.mediaCover.message}</div>
              ) : null}
            </div>
          )}
        />

        <Controller
          control={control}
          name="mediaIntro"
          render={({ field }) => (
            <div className={styles.mediaColumn}>
              <FieldLabel text="Intro video" />
              <div className={styles.mediaBox}>
                <MediaDropField
                  value={field.value}
                  onChange={field.onChange}
                  format="video"
                  accept={{ 'video/*': [] }}
                  disabled={isSubmitting}
                  testId="program-form-intro"
                />
              </div>
            </div>
          )}
        />

        <Controller
          control={control}
          name="color"
          render={({ field }) => (
            <div className={styles.colorColumn}>
              <FieldLabel text="Primary color" />
              <ProgramColorField
                value={field.value}
                onChange={field.onChange}
                disabled={isSubmitting}
              />
            </div>
          )}
        />
      </div>
    </CollapsibleSection>
  );
};
